import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CALLBACK_STATUSES, dateOnly, dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Callbacks() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ status: "", scheduled_at: "", assigned_to: "", notes: "" });
  const { data, isLoading } = useQuery({
    queryKey: ["callbacks", page, status],
    queryFn: () => get("/callbacks", { page, page_size: 25, status: status || undefined }),
    refetchInterval: 15000,
  });
  const { data: stats } = useQuery({ queryKey: ["callback-stats"], queryFn: () => get("/callbacks/stats") });
  const { data: users } = useQuery({ queryKey: ["assignable"], queryFn: () => get("/users/assignable") });

  const update = useMutation({
    mutationFn: ({ id, payload }) => api.patch(`/callbacks/${id}`, payload),
    onSuccess: () => {
      toast.success("Callback updated");
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["callbacks"] });
      queryClient.invalidateQueries({ queryKey: ["callback-stats"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const rows = data?.items || [];
  const overdue = rows.filter((r) => r.overdue);
  const localDay = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  };
  const grouped = rows.reduce((acc, row) => {
    const day = localDay(row.scheduled_at);
    acc[day] = acc[day] || [];
    acc[day].push(row);
    return acc;
  }, {});

  return (
    <>
      <PageHeader testId="callbacks-page" title="Callbacks" subtitle="The AI agent calls at the day and time agreed in the conversation" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiTile testId="cb-kpi-pending" label="Pending" value={stats?.pending ?? 0} />
        <KpiTile testId="cb-kpi-overdue" label="Overdue" value={stats?.overdue ?? 0} tone="danger" />
        <KpiTile testId="cb-kpi-today" label="Today" value={stats?.today ?? 0} tone="primary" />
        <KpiTile testId="cb-kpi-completed" label="Completed" value={stats?.completed ?? 0} tone="success" />
        <KpiTile testId="cb-kpi-missed" label="Missed" value={stats?.missed ?? 0} tone="warning" />
      </div>

      {overdue.length ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5" data-testid="callbacks-overdue-banner">
          <p className="text-sm font-semibold text-rose-800">{overdue.length} callbacks are past the agreed time</p>
          <p className="text-xs text-rose-700">
            {overdue.slice(0, 4).map((c) => `${c.contact_name} (${dt(c.scheduled_at)})`).join(" · ")}
          </p>
        </div>
      ) : null}

      <Section
        testId="callbacks-calendar"
        title="Calendar view"
        subtitle="Grouped by scheduled day"
      >
        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.keys(grouped).length === 0 ? (
            <p className="text-sm text-slate-500">No callbacks scheduled.</p>
          ) : (
            Object.entries(grouped).map(([day, items]) => (
              <div key={day} className="rounded-lg border border-slate-200">
                <p className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
                  {items[0]?.scheduled_at ? dateOnly(items[0].scheduled_at) : "Unscheduled"}
                </p>
                <ul className="divide-y divide-slate-100">
                  {items.map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">{item.contact_name}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(item.scheduled_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · {item.product || "—"}
                        </p>
                      </div>
                      <StatusBadge value={item.overdue ? "missed" : item.status} label={item.overdue ? "Overdue" : title(item.status)} />
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </Section>

      <Section
        testId="callbacks-list"
        title={`${data?.total ?? 0} callbacks`}
        actions={
          <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger data-testid="callbacks-status-filter" className="h-9 w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              {CALLBACK_STATUSES.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      >
        <DataTable
          testId="callbacks-table"
          rowTestId="callback-row"
          loading={isLoading}
          columns={[
            { key: "contact_name", label: "Contact", render: (r) => <span className="font-medium text-slate-800">{r.contact_name}</span> },
            { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
            { key: "scheduled_at", label: "Scheduled", render: (r) => dt(r.scheduled_at) },
            { key: "timezone", label: "Timezone" },
            { key: "product", label: "Product", render: (r) => r.product || "—" },
            { key: "reason", label: "Reason", render: (r) => (
              <span data-testid={`callback-reason-${r.id}`} className="text-xs">
                {r.reason}
                {r.scheduled_by_customer ? (
                  <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                    agreed time
                  </span>
                ) : null}
                {r.dispatch_error ? <span className="mt-0.5 block text-rose-600">{r.dispatch_error}</span> : null}
              </span>
            ) },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.overdue ? "missed" : r.status} label={r.overdue ? "Overdue" : title(r.status)} /> },
            {
              key: "actions",
              label: "",
              render: (r) => (
                <div className="flex items-center gap-1">
                {can("callbacks", "write") ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`callback-edit-${r.id}`}
                    onClick={() => {
                      setEditing(r);
                      setForm({
                        status: r.status,
                        scheduled_at: r.scheduled_at
                          ? new Date(new Date(r.scheduled_at).getTime() - new Date().getTimezoneOffset() * 60000)
                              .toISOString().slice(0, 16)
                          : "",
                        assigned_to: r.assigned_to || "",
                        notes: r.notes || "",
                      });
                    }}
                  >
                    Manage
                  </Button>
                ) : null}
                </div>
              ),
            },
          ]}
          rows={rows}
          empty={{ title: "No callbacks", description: "When a customer agrees a day and time, the AI agent calls then." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Manage callback · {editing?.contact_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="callback-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {CALLBACK_STATUSES.map((s) => <SelectItem key={s} value={s}>{title(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Reschedule to (your local time)</Label>
              <Input
                data-testid="callback-datetime"
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Assigned user</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="callback-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {(users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input data-testid="callback-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() =>
                update.mutate({
                  id: editing.id,
                  payload: {
                    status: form.status,
                    scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : undefined,
                    assigned_to: form.assigned_to || null,
                    notes: form.notes,
                  },
                })
              }
              data-testid="callback-save"
            >
              Save callback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}